// Catalog of admin-managed site assets, the source of truth for
// which keys exist and what each one is for.
//
// The admin UI in `AdminSiteAssetsPanel` lists every entry here as a
// card; consumers in pages/components call `useSiteAsset(key)` to
// resolve the live URL. No bundled fallbacks: when no upload exists,
// the consumer renders nothing and the surrounding gradient / page
// background carries the layout.

import { SOCIAL_PAGES, isSocialPageId, socialPage, type SocialPageId } from "@/lib/siteCopy";

export type MediaType = "image" | "video";

export interface SiteAssetRecommendation {
  width: number;
  height: number;
  /** Soft cap surfaced as a warning. */
  maxBytes: number;
  /** Lower-cased, e.g. ['webp', 'jpg']. */
  formats: string[];
}

export interface SiteAssetSpec {
  /** Stable identifier, matches the `key` column in public.site_assets. */
  key: string;
  /** Human-readable name shown in the admin panel. */
  label: string;
  /** Page grouping for the admin UI ('Home', 'About', 'Areas', etc.). */
  page: string;
  /** One-line description so admins know what they're replacing. */
  description: string;
  mediaType: MediaType;
  recommendation: SiteAssetRecommendation;
  /** Reserved for an optional bundled fallback. Empty today, every
   *  asset is admin-uploaded. */
  fallback: string;
  /** Serve the uploaded file as-is instead of through the image
   *  transformer (SVG logos, favicons). */
  raw?: boolean;
  /** When set, uploads go through a fixed-ratio crop and are stored at
   *  exactly `output` size (every photo; not logos, icons or video). */
  crop?: SiteAssetCrop;
  /** The shapes the picture is shown in on the live site, first = main.
   *  The admin previews it in exactly these, so admin and site match. */
  frames?: AssetFrame[];
}

export interface AssetFrame {
  label: string;
  /** Tailwind aspect class, the same one the live page uses (FRAME). */
  className: string;
  /** Phone-shaped frames are previewed narrow. */
  narrow?: boolean;
}

/**
 * The aspect of every slot a site photo appears in. Live pages use these
 * constants and the admin previews with them, so the two cannot drift
 * (src/test/siteAssets.test.ts checks the pages import them).
 */
export const FRAME = {
  /** Home "For renters" / "For owners" images. */
  feature: "aspect-[4/3]",
  /** Area cards on Explore Areas and "nearby areas". */
  areaCard: "aspect-[4/3]",
  /** Area page header on a computer (on a phone it is ~3:2, FRAME.areaHeaderPhone). */
  areaHeaderDesktop: "aspect-[4/1]",
  areaHeaderPhone: "aspect-[3/2]",
  /** Full-screen backgrounds (home hero, about) on a computer and on a phone. */
  desktop: "aspect-[16/9]",
  phone: "aspect-[9/16]",
  /** Link previews (Open Graph). */
  social: "aspect-[1200/630]",
} as const;

/** A photo crop at the asset's recommended size: JPEG, sources down to a third of it accepted. */
export const photoCrop = (width: number, height: number, quality = 0.85): SiteAssetCrop => ({
  aspect: width / height,
  output: { width, height, mime: "image/jpeg", quality },
  minSource: { width: Math.round(width / 3), height: Math.round(height / 3) },
});

const FULLSCREEN_FRAMES: AssetFrame[] = [
  { label: "Computer", className: FRAME.desktop },
  { label: "Phone", className: FRAME.phone, narrow: true },
];
const SOCIAL_FRAMES: AssetFrame[] = [{ label: "When a link is shared", className: FRAME.social }];

export interface SiteAssetCrop {
  aspect: number;
  output: { width: number; height: number; mime: "image/jpeg"; quality: number };
  /** Smallest source the crop accepts; below this the upload is refused. */
  minSource: { width: number; height: number };
}

/** Open Graph cards: 1.91:1, stored as 1200×630 JPEG so every scraper gets
 *  the same bytes and og:image:width/height can be stated. */
export const SOCIAL_CARD_CROP: SiteAssetCrop = {
  aspect: 1200 / 630,
  output: { width: 1200, height: 630, mime: "image/jpeg", quality: 0.86 },
  minSource: { width: 600, height: 315 },
};

/** Hard upload cap, files larger than this are blocked regardless
 *  of the per-asset recommendation. Kept generous so operators can
 *  upload large videos; the storage bucket enforces its own limit. */
export const SITE_ASSET_HARD_LIMIT_BYTES = 500 * 1024 * 1024; // 500MB

export const SITE_ASSETS: SiteAssetSpec[] = [
  {
    key: "home.hero",
    label: "Home hero",
    page: "Home",
    description:
      "The fullscreen background image on the homepage. The largest image on the site, keep it punchy and well-compressed.",
    mediaType: "image",
    recommendation: {
      width: 1920,
      height: 1080,
      maxBytes: 500_000,
      formats: ["webp", "jpg", "png", "gif"],
    },
    fallback: "",
    crop: photoCrop(1920, 1080),
    frames: FULLSCREEN_FRAMES,
  },
  {
    key: "home.heroVideo",
    label: "Home hero, video (optional)",
    page: "Home",
    description:
      "Optional looping background video on the homepage. When set, it replaces the hero image. Shoot it portrait 9:16 for mobile; keep it short (≤15s), muted, well-compressed, videos cost LCP.",
    mediaType: "video",
    recommendation: {
      width: 1080,
      height: 1920,
      maxBytes: 6_000_000, // ~6MB: enough bitrate for a crisp short portrait loop
      formats: ["mp4", "webm"],
    },
    fallback: "",
  },
  {
    key: "home.feature.renter",
    label: "Home, \"For renters\" feature image",
    page: "Home",
    description: "Right-side image in the 'For renters' section of the homepage.",
    mediaType: "image",
    recommendation: {
      width: 1200,
      height: 900,
      maxBytes: 200_000,
      formats: ["webp", "jpg", "png", "gif"],
    },
    fallback: "",
    crop: photoCrop(1200, 900),
    frames: [{ label: "On the home page", className: FRAME.feature }],
  },
  {
    key: "home.feature.owner",
    label: "Home, \"For owners\" feature image",
    page: "Home",
    description: "Left-side image in the 'For owners' section of the homepage.",
    mediaType: "image",
    recommendation: {
      width: 1200,
      height: 900,
      maxBytes: 200_000,
      formats: ["webp", "jpg", "png", "gif"],
    },
    fallback: "",
    crop: photoCrop(1200, 900),
    frames: [{ label: "On the home page", className: FRAME.feature }],
  },
  {
    key: "about.bg",
    label: "About page background",
    page: "About",
    description: "Fullscreen-fixed background on the About page.",
    mediaType: "image",
    recommendation: {
      width: 1920,
      height: 1280,
      maxBytes: 400_000,
      formats: ["webp", "jpg", "png", "gif"],
    },
    fallback: "",
    crop: photoCrop(1920, 1280),
    frames: FULLSCREEN_FRAMES,
  },
  {
    key: "seo.ogImage",
    label: "Site share card",
    page: "Social sharing",
    description:
      "The image shown when a link to this site is shared on WhatsApp, Facebook, iMessage, LinkedIn… Pages with their own card or image (listings, areas) keep it. Falls back to the Tenants → social card URL, then the built-in neutral card.",
    mediaType: "image",
    recommendation: {
      width: 1200,
      height: 630,
      maxBytes: 300_000,
      formats: ["jpg", "png", "webp"],
    },
    fallback: "",
    crop: SOCIAL_CARD_CROP,
    frames: SOCIAL_FRAMES,
  },
  {
    key: "brand.logo",
    label: "Logo (top bar)",
    page: "Brand",
    description:
      "Wordmark shown at the left of the top bar, about 32px tall. Wide transparent SVG, PNG or WebP works best. Nothing is shown until you upload one.",
    mediaType: "image",
    recommendation: {
      width: 640,
      height: 160,
      maxBytes: 100_000,
      formats: ["svg", "png", "webp"],
    },
    fallback: "",
    raw: true,
  },
  {
    key: "brand.favicon",
    label: "Favicon / app icon",
    page: "Brand",
    description: "Square icon for browser tabs and phone home screens. 512×512 PNG with a plain background.",
    mediaType: "image",
    recommendation: {
      width: 512,
      height: 512,
      maxBytes: 100_000,
      formats: ["png", "webp"],
    },
    fallback: "",
    raw: true,
  },
];

/** Map for O(1) lookup by key. */
export const SITE_ASSETS_BY_KEY: Record<string, SiteAssetSpec> = Object.fromEntries(
  SITE_ASSETS.map((a) => [a.key, a]),
);

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
};

/**
 * Pick the row for `key` under `tenantId` (null = the global asset). Matching
 * is NULL-aware and the newest `updated_at` wins, so the duplicate rows the old
 * upsert left behind (a NULL tenant_id never conflicted, so every "replace"
 * inserted a fresh row) can never surface an older upload again.
 */
export function pickSiteAssetRow<T extends { key: string; tenant_id: string | null; updated_at?: string | null }>(
  rows: readonly T[] | undefined,
  key: string,
  tenantId: string | null,
): T | undefined {
  const ts = (r: T) => Date.parse(r.updated_at ?? "") || 0;
  let best: T | undefined;
  for (const r of rows ?? []) {
    if (r.key !== key || (r.tenant_id ?? null) !== (tenantId ?? null)) continue;
    if (!best || ts(r) > ts(best)) best = r;
  }
  return best;
}

// ---- Area photos ------------------------------------------------------------
// One image per neighbourhood, keyed `areas.<slug>.hero` and scoped to the
// city it belongs to. The catalog above is static; these specs are generated
// from the tenant's area list so a city added in the admin gets picture slots
// without a code change.
const AREA_ASSET_PREFIX = "areas.";
const AREA_ASSET_SUFFIX = ".hero";

export const areaAssetKey = (slug: string): string => `${AREA_ASSET_PREFIX}${slug}${AREA_ASSET_SUFFIX}`;

/** The area slug behind an `areas.<slug>.hero` key, else null. */
export const areaSlugFromAssetKey = (key: string): string | null => {
  if (!key.startsWith(AREA_ASSET_PREFIX) || !key.endsWith(AREA_ASSET_SUFFIX)) return null;
  const slug = key.slice(AREA_ASSET_PREFIX.length, key.length - AREA_ASSET_SUFFIX.length);
  return slug || null;
};

export const areaAssetSpec = (slug: string, name: string = slug): SiteAssetSpec => ({
  key: areaAssetKey(slug),
  label: `${name}, area photo`,
  page: "Areas",
  description: `Card image on Explore Areas, the hero of the ${name} page, and the share image for that page.`,
  mediaType: "image",
  recommendation: {
    width: 1600,
    height: 1000,
    maxBytes: 350_000,
    formats: ["webp", "jpg", "png"],
  },
  fallback: "",
  crop: photoCrop(1600, 1000),
  frames: [
    { label: "Explore Areas card", className: FRAME.areaCard },
    { label: "Area page, computer", className: FRAME.areaHeaderDesktop },
    { label: "Area page, phone", className: FRAME.areaHeaderPhone, narrow: true },
  ],
});

// ---- Per-page share cards ---------------------------------------------------
// One optional card per public page, keyed `og.page.<id>` for the pages in
// SOCIAL_PAGES. A page without its own card shares the site card.
const OG_PAGE_PREFIX = "og.page.";

export const ogPageAssetKey = (id: SocialPageId): string => `${OG_PAGE_PREFIX}${id}`;

/** The page id behind an `og.page.<id>` key, else null. */
export const ogPageIdFromAssetKey = (key: string): SocialPageId | null => {
  if (!key.startsWith(OG_PAGE_PREFIX)) return null;
  const id = key.slice(OG_PAGE_PREFIX.length);
  return isSocialPageId(id) ? id : null;
};

export const ogPageAssetSpec = (id: SocialPageId): SiteAssetSpec => {
  const page = socialPage(id);
  return {
    key: ogPageAssetKey(id),
    label: `${page.label}, share card`,
    page: "Social sharing",
    description: `Shown when ${page.path} is shared. Leave empty to share the site card.`,
    mediaType: "image",
    recommendation: {
      width: 1200,
      height: 630,
      maxBytes: 300_000,
      formats: ["jpg", "png", "webp"],
    },
    fallback: "",
    crop: SOCIAL_CARD_CROP,
    frames: SOCIAL_FRAMES,
  };
};

/** Generated specs for every page card, in page order. */
export const SOCIAL_PAGE_ASSET_SPECS: SiteAssetSpec[] = SOCIAL_PAGES.map((p) => ogPageAssetSpec(p.id));

/** Static catalog entry or a generated area / page-card spec; undefined for unknown keys. */
export function resolveSiteAssetSpec(key: string): SiteAssetSpec | undefined {
  const fixed = SITE_ASSETS_BY_KEY[key];
  if (fixed) return fixed;
  const slug = areaSlugFromAssetKey(key);
  if (slug) return areaAssetSpec(slug);
  const pageId = ogPageIdFromAssetKey(key);
  return pageId ? ogPageAssetSpec(pageId) : undefined;
}

/**
 * What the admin sees for `key` under a scope: a city scope shows its own
 * row if present, else the inherited global one (the precedence the site
 * uses). `scopedRow` drives the Custom pill and Reset, so admins only edit
 * or clear the asset for the selected scope.
 */
export function scopedAssetView<T extends { key: string; tenant_id: string | null; updated_at?: string | null }>(
  rows: readonly T[] | undefined,
  key: string,
  scope: string | null,
): { displayRow: T | undefined; scopedRow: T | undefined; isInherited: boolean } {
  const scopedRow = pickSiteAssetRow(rows, key, scope);
  const globalRow = pickSiteAssetRow(rows, key, null);
  const displayRow = scope === null ? globalRow : scopedRow ?? globalRow;
  return { displayRow, scopedRow, isInherited: scope !== null && !scopedRow && !!globalRow };
}
